import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { TextField } from 'seagull';
import { isValidHexColor } from '~/business-logic/colorsLogic';
import { useLocaleKeys } from '~/i18n';
import { ColorSelector } from '../ColorSelector/ColorSelector';
import styles from './ColorPicker.module.scss';
/**
 * Renders a text input for HEX values with a preview on the right for the color,
 * which is also a native color picker when clicked.
 */ export function ColorPicker(props) {
    const { color, className, testId, selected, displayColor, onChange, onBlur, label, colorAsSuffix, inputWrapperClassName, noMinWidth } = props;
    const { t } = useLocaleKeys('components.colorPicker');
    const colorInputRef = useRef(null);
    const isValidColor = isValidHexColor(color);
    useEffect(()=>{
        if (onBlur && colorInputRef.current) {
            const inputEl = colorInputRef.current;
            inputEl.addEventListener('change', onBlur);
            return ()=>{
                inputEl.removeEventListener('change', onBlur);
            };
        }
    }, [
        onBlur
    ]);
    const colorSwatch = /*#__PURE__*/ _jsx(ColorSelector, {
        withBorder: true,
        hasError: !isValidColor,
        selected: selected,
        color: displayColor || 'linear-gradient(180deg, #EB3563 0%, #6564F6 51.56%, #84F96B 100%)',
        onClick: ()=>{
            var _colorInputRef_current;
            return (_colorInputRef_current = colorInputRef.current) === null || _colorInputRef_current === void 0 ? void 0 : _colorInputRef_current.click();
        }
    });
    const suffix = colorAsSuffix ? /*#__PURE__*/ _jsx("span", {
        className: styles.suffixTrigger,
        onClick: ()=>{
            var _colorInputRef_current;
            return (_colorInputRef_current = colorInputRef.current) === null || _colorInputRef_current === void 0 ? void 0 : _colorInputRef_current.click();
        },
        role: "button",
        tabIndex: 0,
        onKeyDown: (e)=>{
            var _colorInputRef_current;
            return e.key === 'Enter' && ((_colorInputRef_current = colorInputRef.current) === null || _colorInputRef_current === void 0 ? void 0 : _colorInputRef_current.click());
        },
        children: colorSwatch
    }) : null;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.root, className, colorAsSuffix && styles.rootSuffix, noMinWidth && styles.noMinWidth),
        "data-testid": testId !== null && testId !== void 0 ? testId : undefined,
        children: [
            colorAsSuffix ? /*#__PURE__*/ _jsx("span", {
                className: cn(styles.fullWidthInputWrapper, inputWrapperClassName),
                children: /*#__PURE__*/ _jsx(TextField, {
                    size: "small",
                    prefix: "#",
                    suffix: suffix,
                    disableSuffixPadding: true,
                    label: label || 'Customize color',
                    testId: testId && "".concat(testId, "-text"),
                    value: color.slice(1),
                    error: !isValidColor,
                    helperText: isValidColor ? '' : t.invalidHexHelperText(),
                    onChange: (e)=>onChange("#".concat(e.target.value)),
                    onBlur: onBlur
                })
            }) : /*#__PURE__*/ _jsx(TextField, {
                size: "small",
                prefix: "#",
                label: label || 'Customize color',
                testId: testId && "".concat(testId, "-text"),
                value: color.slice(1),
                error: !isValidColor,
                helperText: isValidColor ? '' : t.invalidHexHelperText(),
                onChange: (e)=>onChange("#".concat(e.target.value)),
                onBlur: onBlur
            }),
            colorAsSuffix ? /*#__PURE__*/ _jsx("input", {
                ref: colorInputRef,
                className: styles.input,
                type: "color",
                value: color,
                onChange: (e)=>onChange(e.target.value),
                "aria-hidden": true,
                tabIndex: -1
            }) : /*#__PURE__*/ _jsxs("div", {
                className: styles.previewWrapper,
                children: [
                    colorSwatch,
                    /*#__PURE__*/ _jsx("input", {
                        ref: colorInputRef,
                        className: styles.input,
                        type: "color",
                        value: color,
                        onChange: (e)=>onChange(e.target.value)
                    })
                ]
            })
        ]
    });
}
